import React from "react";
import { useLanguage } from "../../../../contexts";
import { P, useEnergia, useGuardarSeccion } from "../engine";
import { useBorrador } from "../common/useBorrador";
import { EditableTable } from "../common/EditableTable";
import { Notice, SaveBar, SaveErrors, Section, StatusBadge } from "../common/SectionCommon";
import { GraficoCatalogo } from "../common/Grafico";
import "../common/common.scss";

const CATEGORIAS = [
  ["Productivo", "energy_performance_ops_productive", "energy_performance_ops_productive_note"],
  ["Auxiliar", "energy_performance_ops_auxiliary", null],
  ["Doméstico", "energy_performance_ops_domestic", "energy_performance_ops_domestic_note"],
];
const tomar = (inst) => ({ operations: inst.operations || [] });
const fmt = (v, d = 0) => P.fmt(v, d);
const ESTADO_EVAL = { "eval-ok": "Cumple", "eval-bad": "Brecha parcial", "eval-warn": "Pendiente" };

// Reemplaza en la lista completa las filas de una categoría, sin cambiar el orden de las demás
// (el orden de las operaciones importa: la cascada de medidas sigue el orden del archivo).
function reemplazarCategoria(lista, cat, nuevas) {
  const out = [];
  let k = 0;
  lista.forEach((o) => {
    if (o.cat !== cat) out.push(o);
    else if (k < nuevas.length) out.push(nuevas[k++]);
    else k++; // borrada
  });
  while (k < nuevas.length) out.push(nuevas[k++]); // agregadas
  return out;
}

// Desempeño energético por proceso y operación (hoja 01 filas 11, 13, 23, 112, 123): solo contra la meta propia.
export function ProcessPerformance({ installation, onSaved }) {
  const { translations } = useLanguage();
  const t = (key) => translations[key] || key;
  const { inst, f, error } = useEnergia(installation);
  const { borrador: b, cambiar, dirty, descartar, marcarInvalidos, invalidos, version } = useBorrador(inst, tomar);
  const { guardar, saving, errores } = useGuardarSeccion(installation, onSaved, { t });

  if (!installation) return <p>{t("energy_performance_create_profile_first")}</p>;
  if (!b) return null;

  const lista = b.operations;
  const totales = CATEGORIAS.map(([c]) => lista.filter((o) => o.cat === c).reduce((a, o) => a + (Number(o.consumo) || 0), 0));
  const total = totales.reduce((a, x) => a + x, 0) || 1;
  const sinMeta = lista.filter((o) => P.evalOperacion(o).ref === "ninguna").length;

  const columnas = [
    { key: "nombre", label: t("energy_performance_ops_operation") },
    { key: "cat", label: t("energy_performance_ops_category"), type: "select", options: CATEGORIAS.map(([c, k]) => [c, t(k)]) },
    { key: "fuente", label: t("energy_performance_mach_source") },
    {
      key: "tipoFuente", label: t("energy_performance_ops_source_type"), type: "select",
      options: [["", t("energy_performance_ops_source_detect")], ["elec", t("energy_performance_ops_source_elec")], ["term", t("energy_performance_ops_source_term")], ["mixta", t("energy_performance_ops_source_mixed")]],
    },
    { key: "consumo", label: t("energy_performance_ops_consumption"), type: "num" },
    { key: "unidad", label: t("energy_performance_ops_unit") },
    { key: "cantidad", label: t("energy_performance_ops_quantity"), type: "num" },
    { key: "_esp", label: t("energy_performance_ops_specific"), type: "view", render: (o) => <span className="ep-mono">{fmt(P.evalOperacion(o).esp, 3)} kWh/{o.unidad || "u"}</span> },
    { key: "meta", label: t("energy_performance_ops_target"), type: "num" },
    { key: "criterio", label: t("energy_performance_ops_criterion"), type: "area" },
    { key: "fracElec", label: t("energy_performance_ops_elec_fraction"), type: "num" },
    {
      key: "_eval", label: t("energy_performance_ops_evaluation"), type: "view", render: (o) => {
        const ev = P.evalOperacion(o);
        const tf = P.tipoFuenteDe(o);
        return (
          <>
            <StatusBadge estado={ESTADO_EVAL[ev.cls] || "Pendiente"}>{ev.label}</StatusBadge>
            <div>{ev.nota}</div>
            {tf === null && <StatusBadge estado="Brecha parcial">{t("energy_performance_ops_source_unknown")}</StatusBadge>}
            {tf === "mixta" && (o.fracElec == null || o.fracElec === "") && <StatusBadge estado="Brecha parcial">{t("energy_performance_ops_fraction_missing")}</StatusBadge>}
          </>
        );
      },
    },
  ];

  return (
    <div className="ep-screen">
      <h3 className="ep-screen__title">{t("energy_performance_ops_title")}</h3>
      <p className="ep-screen__intro">{t("energy_performance_ops_intro")}</p>
      {error && <Notice tipo="danger" titulo={t("energy_performance_data_invalid")}>{error}</Notice>}

      <div className="ep-kpis">
        {CATEGORIAS.map(([c, k], i) => (
          <div className="ep-kpi" key={c}>
            <div className="ep-kpi__label">{t(k)}</div>
            <div className="ep-kpi__value">{fmt(totales[i] / 1000, 1)}</div>
            <div className="ep-kpi__sub">MWh/{t("energy_performance_year")} · {fmt((totales[i] / total) * 100, 1)}%</div>
          </div>
        ))}
      </div>
      {f && <div className="ep-charts"><GraficoCatalogo f={f} id="ops_meta" t={t} /><GraficoCatalogo f={f} id="ops_consumo" t={t} /><GraficoCatalogo f={f} id="costo_ops" t={t} /><GraficoCatalogo f={f} id="huella_ops" t={t} /></div>}
      {sinMeta > 0 && <Notice>{t("energy_performance_ops_without_target").replace("{n}", sinMeta)}</Notice>}

      {CATEGORIAS.map(([c, k, nota]) => (
        <Section key={c} title={t(k)} note={nota ? t(nota) : undefined}>
          <EditableTable
            key={`${c}-${version}`}
            t={t}
            rows={lista.filter((o) => o.cat === c)}
            columns={columnas}
            onChange={(nuevas) => cambiar("operations", reemplazarCategoria(lista, c, nuevas))}
            onErrors={(n) => marcarInvalidos(c, n)}
            newRow={() => ({ ...P.ROW_TEMPLATES.operations(), cat: c })}
            addLabel={t("energy_performance_ops_add")}
            emptyText={t("energy_performance_ops_empty")}
          />
        </Section>
      ))}

      <SaveErrors errores={errores} t={t} />
      <SaveBar dirty={dirty} invalidos={invalidos} saving={saving} onSave={() => guardar(b)} onDiscard={descartar} t={t} />
    </div>
  );
}
